import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Bell } from 'lucide-react';
import {
  NOTIFICATIONS_CHANGED_EVENT,
  notificationsApi,
  type AppNotification,
} from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';

const POLL_INTERVAL_MS = 10_000;

function unwrapNotifications(data: AppNotification[] | { results: AppNotification[] }): AppNotification[] {
  if (Array.isArray(data)) return data;
  return data.results ?? [];
}

function notificationPath(n: AppNotification, role: string | null): string {
  if (role === 'doctor') {
    if (n.type === 'conflict' && n.assignment_id) {
      return `/doctor/assignments/${n.assignment_id}`;
    }
    return n.assignment_id ? `/doctor/assignments/${n.assignment_id}` : '/doctor';
  }
  if (n.type === 'reconciliation_finalized' && n.sample_id) {
    return `/admin/samples/${n.sample_id}`;
  }
  // Admin: submission/resubmission notifications carry the assignment so we can
  // jump straight to that doctor's review; otherwise fall back to the sample.
  if (n.sample_id && n.assignment_id) {
    return `/admin/samples/${n.sample_id}/reviews/${n.assignment_id}`;
  }
  if (n.sample_id) {
    return `/admin/samples/${n.sample_id}`;
  }
  return '/admin';
}

export function NotificationBell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [open, setOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await notificationsApi.list();
      setNotifications(unwrapNotifications(data));
    } catch {
      setNotifications([]);
    }
  }, []);

  useEffect(() => {
    void fetchNotifications();
    const interval = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void fetchNotifications();
    };
    const onChanged = () => { void fetchNotifications(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
    };
  }, [fetchNotifications]);

  const unread = notifications.filter((n) => !n.is_read);

  const handleOpen = async (n: AppNotification) => {
    if (!n.is_read) {
      try {
        await notificationsApi.markRead(n.id);
      } catch {
        /* ignore */
      }
    }
    setOpen(false);
    navigate(notificationPath(n, user?.role ?? null));
    void fetchNotifications();
  };

  const markAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      void fetchNotifications();
    } catch {
      /* ignore */
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="size-5" />
          {unread.length > 0 && (
            <Badge className="absolute -top-1 -right-1 size-5 p-0 flex items-center justify-center text-[10px]">
              {unread.length}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex justify-between items-center">
          Notifications
          {unread.length > 0 && (
            <button type="button" className="text-xs text-blue-600" onClick={markAllRead}>
              Mark all read
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {notifications.length === 0 ? (
          <div className="p-4 text-sm text-gray-500 text-center">No notifications</div>
        ) : (
          notifications.slice(0, 10).map((n) => (
            <DropdownMenuItem key={n.id} onClick={() => handleOpen(n)} className="flex flex-col items-start gap-1">
              <span className={n.is_read ? 'text-gray-600' : 'font-medium'}>{n.message}</span>
              <span className="text-xs text-gray-400">
                {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
              </span>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
